import { HeaderPageTitle } from '@/context/header-title.context';
import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import type { NamespaceContentsResponse } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useNamespaceContents } from '@/api/rpc/hooks/namespaces/useNamespaces';
import { sectionPath } from '@/pages/authenticated/settings/organization/organizationSettingsSections';

type Member = NamespaceContentsResponse['workflows'][number];

/**
 * `/namespaces/$namespaceId` — what one namespace holds: how many tasks it
 * has numbered, and the task types, workflows and agent definitions that
 * belong to it, plus the viewer's own workbooks in it (`namespaces.getContents`).
 * Each member links to its own page.
 */
export function NamespaceDetailPage({ namespaceId }: { namespaceId: string }) {
  const gt = useGT();
  const { data, isLoading, error } = useNamespaceContents(namespaceId);

  if (error) {
    return (
      <div className="mx-auto w-full max-w-4xl p-4">
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </div>
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-3 p-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const { namespace, taskCount, taskTypes, workflows, agents, workbooks } =
    data;
  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <HeaderPageTitle>{namespace.name}</HeaderPageTitle>
            <Badge variant="outline" className="font-mono">
              {namespace.key}
            </Badge>
            {namespace.isDefault ? (
              <Badge variant="secondary">
                <T context="badge on the organization's default namespace">
                  default
                </T>
              </Badge>
            ) : null}
          </div>
          {namespace.description ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {namespace.description}
            </p>
          ) : null}
        </div>
        <Link
          to="/namespaces"
          className="text-sm text-muted-foreground underline-offset-2 hover:underline"
        >
          <T context="back link from namespace detail to the list">
            All namespaces
          </T>
        </Link>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <MemberCard
          title={gt('Tasks', { context: 'namespace detail card title' })}
          description={gt(
            'Tasks numbered in this namespace ({key}-1, {key}-2, …). Last number issued: {last}.',
            {
              context: 'namespace detail tasks card description',
              key: namespace.key,
              last: String(namespace.lastTaskNumber),
            },
          )}
          count={taskCount}
        >
          <Link
            to="/tasks"
            className="text-sm underline-offset-2 hover:underline"
          >
            <T context="link from namespace detail to the tasks queue">
              Open the tasks queue
            </T>
          </Link>
        </MemberCard>

        <MemberCard
          title={gt('Task types', { context: 'namespace detail card title' })}
          description={gt('Types whose default namespace is this one.', {
            context: 'namespace detail task types card description',
          })}
          count={taskTypes.length}
        >
          <MemberList
            members={taskTypes}
            renderLink={(member) => (
              <Link
                to={sectionPath('task-types')}
                className="underline-offset-2 hover:underline"
              >
                {member.name}
              </Link>
            )}
          />
        </MemberCard>

        <MemberCard
          title={gt('Workflows', { context: 'namespace detail card title' })}
          description={gt('Workflow lineages assigned to this namespace.', {
            context: 'namespace detail workflows card description',
          })}
          count={workflows.length}
        >
          <MemberList
            members={workflows}
            renderLink={(member) => (
              <Link
                to="/workflows/$workflowId"
                params={{ workflowId: member.id }}
                className="underline-offset-2 hover:underline"
              >
                {member.name}
              </Link>
            )}
          />
        </MemberCard>

        <MemberCard
          title={gt('Agents', { context: 'namespace detail card title' })}
          description={gt('Agent definitions assigned to this namespace.', {
            context: 'namespace detail agents card description',
          })}
          count={agents.length}
        >
          <MemberList
            members={agents}
            renderLink={(member) => (
              <Link
                to="/agents/$agentDefinitionId"
                params={{ agentDefinitionId: member.id }}
                className="underline-offset-2 hover:underline"
              >
                {member.name}
              </Link>
            )}
          />
        </MemberCard>

        <MemberCard
          title={gt('Workbooks', { context: 'namespace detail card title' })}
          description={gt(
            'Your workbooks assigned to this namespace. Workbooks are private, so others see only their own.',
            { context: 'namespace detail workbooks card description' },
          )}
          count={workbooks.length}
        >
          <MemberList
            members={workbooks}
            renderLink={(member) => (
              <Link
                to="/workbooks/$workbookId"
                params={{ workbookId: member.id }}
                className="underline-offset-2 hover:underline"
              >
                {member.name}
              </Link>
            )}
          />
        </MemberCard>
      </div>
    </div>
  );
}

function MemberCard({
  title,
  description,
  count,
  children,
}: {
  title: string;
  description: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span>{title}</span>
          <span className="text-2xl font-semibold tabular-nums">{count}</span>
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function MemberList({
  members,
  renderLink,
}: {
  members: Array<
    Pick<Member, 'id' | 'name'> & Partial<Pick<Member, 'isActive'>>
  >;
  renderLink: (member: Pick<Member, 'id' | 'name'>) => ReactNode;
}) {
  if (members.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="empty state inside a namespace member card">None yet.</T>
      </p>
    );
  }
  return (
    <ul className="space-y-1 text-sm">
      {members.map((member) => (
        <li key={member.id} className="flex items-center gap-2">
          {renderLink(member)}
          {member.isActive === false ? (
            <Badge variant="outline" className="text-muted-foreground">
              <T context="badge on an inactive namespace member">inactive</T>
            </Badge>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
